import { Component, EventEmitter, Input, Output, computed, effect, signal } from '@angular/core'
import { DetailsModel } from '../../../shared/models/details.model'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { RoadSegmentPropertiesModel } from 'src/app/road-condition/model/road-segment-properties.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { Warning } from 'src/app/shared/models/road-segment.model'
import { BwcMetrics, getMetricFromMap } from '../../enums/bwc-metrics.enum'
import { Layers } from 'src/app/shared/layers-config/layers'
import { format } from 'date-fns'
import { AppPaths } from 'src/app/app-routing.module'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'

@Component({
  selector: 'app-bwc-details',
  templateUrl: './bwc-details.component.html',
  standalone: false,
})
export class BwcDetailsComponent {
  @Input() isVisible: boolean = false
  @Output() setVisible = new EventEmitter<{ details: boolean }>()
  public metric: string[] | undefined = []
  public BwcMetrics = BwcMetrics
  public Permissions = Permissions
  private readonly _details = signal<Map<string, DetailsModel>>(new Map<string, DetailsModel>())
  public readonly details = this._details.asReadonly()
  public hasDetails = computed(() => this.details().size > 0)
  public rawFeature: string | undefined
  public features: MapGeoJSONFeature[] = []
  public index: number = 0
  public icon = 'rb-ic rb-ic-arrow-down-frame fs-2xl'
  public globalId = ''

  constructor(
    private readonly mapEventService: MapEventsService,
    private appState: AppStateService,
  ) {
    effect(() => {
      const features = this.mapEventService.clickedFeatures()
      this._details.set(new Map<string, DetailsModel>())
      this.features.length != features.length ? (this.index = 0) : this.index
      if (features[this.index]?.layer?.id === Layers.RoadSegment.name) {
        this.setVisible.emit({ details: true })
        this.features = features
        this.metric = this.appState.selectMetrics(Layers.RoadSegment.name)
        this.setCurrentFeature(this.index)
      }
    })
  }

  toggleFeature(index: number) {
    this.index = index
    this.setCurrentFeature(index)
  }

  setCurrentFeature(index: number = this.index) {
    const properties: RoadSegmentPropertiesModel = this.features[index]
      .properties as RoadSegmentPropertiesModel
    this.globalId = this.features[index].id?.toString() ?? ''
    const warnings: any = (this.features[index] as any).state
    const heavyRain = warnings[getMetricFromMap(BwcMetrics.RoadSegment_HeavyRain).name] as Warning
    const heavySnow = warnings[getMetricFromMap(BwcMetrics.RoadSegment_HeavySnow).name] as Warning
    const strongWind = warnings[getMetricFromMap(BwcMetrics.RoadSegment_StrongWind).name] as Warning
    const lowVisibility = warnings[
      getMetricFromMap(BwcMetrics.RoadSegment_LowVisibility).name
    ] as Warning
    const timestamp = warnings.timestamp
    this.mapMetaToDetails(properties.highway, timestamp)
    this.mapRoadConditionToDetails(heavyRain, heavySnow, strongWind, lowVisibility)
    this.appState.setState(AppPaths.BadWeatherCondition, {
      selectedFeatureId: this.globalId,
    })
  }

  private mapMetaToDetails(highway: string, timestamp: number) {
    this.setDetail('Meta', {
      title: 'Road Segment',
      items: [
        {
          description: 'Type',
          value: highway,
        },
        {
          description: 'Segment ID',
          value: this.globalId,
        },
        {
          description: `Last Updated (${getTimeZoneLabel(new Date(timestamp * 1000))})`,
          value: timestamp ? format(new Date(timestamp * 1000), 'yyyy-MM-dd HH:mm:ss') : '-',
        },
      ],
    })
  }

  private mapRoadConditionToDetails(
    heavyRain: Warning,
    heavySnow: Warning,
    strongWind: Warning,
    lowVisibility: Warning,
  ) {
    if (heavyRain) {
      this.setDetail(BwcMetrics.RoadSegment_HeavyRain, {
        title: 'Heavy Rain',
        items: [
          {
            description: 'Probability',
            value: (heavyRain.probability * 100).toFixed(1),
            unit: '%',
          },
          {
            description: 'Uncertainty',
            value: heavyRain.uncertainty.toString(),
            unit: 'mm/h',
          },
          {
            description: 'Value',
            value: heavyRain.value.toString(),
            unit: 'mm/h',
          },
        ],
      })
    }

    if (heavySnow) {
      this.setDetail(BwcMetrics.RoadSegment_HeavySnow, {
        title: 'Heavy Snow',
        items: [
          {
            description: 'Probability',
            value: (heavySnow.probability * 100).toFixed(1),
            unit: '%',
          },
          {
            description: 'Uncertainty',
            value: heavySnow.uncertainty.toString(),
            unit: 'mm/h',
          },
          {
            description: 'Value',
            value: heavySnow.value.toString(),
            unit: 'mm/h',
          },
        ],
      })
    }

    if (strongWind) {
      this.setDetail(BwcMetrics.RoadSegment_StrongWind, {
        title: 'Strong Wind',
        items: [
          {
            description: 'Probability',
            value: (strongWind.probability * 100).toFixed(1),
            unit: '%',
          },
          {
            description: 'Uncertainty',
            value: strongWind.uncertainty.toString(),
            unit: 'm/s',
          },
          {
            description: 'Value',
            value: strongWind.value.toString(),
            unit: 'm/s',
          },
        ],
      })
    }

    if (lowVisibility) {
      this.setDetail(BwcMetrics.RoadSegment_LowVisibility, {
        title: 'Low Visibility',
        items: [
          {
            description: 'Probability',
            value: (lowVisibility.probability * 100).toFixed(1),
            unit: '%',
          },
          {
            description: 'Uncertainty',
            value: lowVisibility.uncertainty.toString(),
            unit: 'm',
          },
          {
            description: 'Value',
            value: lowVisibility.value.toString(),
            unit: 'm',
          },
        ],
      })
    }
  }

  private setDetail(key: string, value: DetailsModel): void {
    this._details.update((current) => {
      const next = new Map(current)
      next.set(key, value)
      return next
    })
  }

  close() {
    this._details.set(new Map<string, DetailsModel>())
    this.appState.setState(AppPaths.BadWeatherCondition, {
      selectedFeatureId: undefined,
    })
  }
}
